"use client";

import dynamic from "next/dynamic";
import { useEffect, useRef, useState, type ReactNode } from "react";

import type { CompanyReviewCollection } from "@/types/companyReview";

const DownloadApp = dynamic(() => import("@/app/components/DownloadApp/DownloadApp"), {
	loading: () => <HomeBlockPlaceholder className="h-64" />,
});
const Testimonials = dynamic(() => import("@/app/components/Testimonials/Testimonials"), {
	loading: () => <HomeBlockPlaceholder className="h-96" />,
});
const Talent = dynamic(() => import("@/app/components/Talent/Talent"), {
	loading: () => <HomeBlockPlaceholder className="h-64" />,
});
const PostAJob = dynamic(() => import("@/app/components/PostAJob/PostAJob"), {
	loading: () => <HomeBlockPlaceholder className="h-72" />,
});

function HomeBlockPlaceholder({ className }: { className: string }) {
	return <div className={`${className} animate-pulse bg-secondary/5`} aria-hidden />;
}

export function ViewportDeferredBlock({
	children,
	placeholder,
	rootMargin = "700px 0px",
}: {
	children: ReactNode;
	placeholder: ReactNode;
	rootMargin?: string;
}) {
	const ref = useRef<HTMLDivElement>(null);
	const [shouldRender, setShouldRender] = useState(false);

	useEffect(() => {
		if (shouldRender) return;
		const node = ref.current;
		if (!node) return;

		if (!("IntersectionObserver" in window)) {
			setShouldRender(true);
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					setShouldRender(true);
					observer.disconnect();
				}
			},
			{ rootMargin },
		);

		observer.observe(node);
		return () => observer.disconnect();
	}, [rootMargin, shouldRender]);

	return <div ref={ref}>{shouldRender ? children : placeholder}</div>;
}

export function DeferredDownloadApp() {
	return (
		<ViewportDeferredBlock placeholder={<HomeBlockPlaceholder className="h-64" />}>
			<DownloadApp />
		</ViewportDeferredBlock>
	);
}

export function DeferredTestimonials({
	initialReviews,
	viewAllMode,
	reviewLimit,
}: {
	initialReviews?: CompanyReviewCollection | null;
	viewAllMode?: "link" | "modal";
	reviewLimit?: number;
}) {
	return (
		<ViewportDeferredBlock placeholder={<HomeBlockPlaceholder className="h-96" />}>
			<Testimonials initialReviews={initialReviews} viewAllMode={viewAllMode} reviewLimit={reviewLimit} />
		</ViewportDeferredBlock>
	);
}

export function DeferredTalent() {
	return (
		<ViewportDeferredBlock placeholder={<HomeBlockPlaceholder className="h-64" />}>
			<Talent />
		</ViewportDeferredBlock>
	);
}

export function DeferredPostAJob() {
	return (
		<ViewportDeferredBlock placeholder={<HomeBlockPlaceholder className="h-72" />}>
			<PostAJob />
		</ViewportDeferredBlock>
	);
}
